/**
 * The MRC viewable-impression rule, as `components/placements/Placement.tsx`
 * applies it with an IntersectionObserver: the creative counts as viewable
 * once this has held for a continuous `VIEWABLE_MS`.
 *
 * - ≥ 50 % of the box in view for a normal box;
 * - ≥ 30 % for a large box (≥ 242 500 px² — the IAB/MRC large-ad rule);
 * - measured against the SMALLER of box and viewport, so a half-page taller
 *   than a phone screen can still qualify instead of never (a plain
 *   `intersectionRatio >= 0.5` test could not be met by such a box).
 *
 * Pure, so it is unit-tested without a DOM.
 */
export const VIEWABLE_MS = 1000;
const LARGE_BOX_PX2 = 242_500;

/** Every 5 % of the box, so the rule is re-evaluated as it scrolls in. */
export const VIEWABILITY_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

export interface ViewabilityEntry {
  boundingClientRect: { width: number; height: number };
  intersectionRect: { width: number; height: number };
  rootBounds: { width: number; height: number } | null;
}

export function meetsViewabilityRule(
  entry: ViewabilityEntry,
  /** The viewport size when the observer has no `rootBounds` (cross-origin
   *  iframes); the caller passes `window`'s. */
  fallbackViewport?: { width: number; height: number },
): boolean {
  const box = entry.boundingClientRect.width * entry.boundingClientRect.height;
  if (box <= 0) return false;
  const bounds = entry.rootBounds ?? fallbackViewport;
  const viewport = bounds ? bounds.width * bounds.height : box;
  const visible = entry.intersectionRect.width * entry.intersectionRect.height;
  const needed = box >= LARGE_BOX_PX2 ? 0.3 : 0.5;
  return visible / Math.min(box, viewport || box) >= needed;
}
